import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../Firebase';
import { collection, getDocs } from "firebase/firestore";
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import EventNoteIcon from '@mui/icons-material/EventNote';
import './Timetable.css';

const Timetable = () => {
    const [loading, setLoading] = useState(true);
    const [timetableData, setTimetableData] = useState([]);
    const [courseCatalog, setCourseCatalog] = useState({});
    const [receiptMap, setReceiptMap] = useState({});
    const [selectedCourse, setSelectedCourse] = useState('All');
    const [selectedDay, setSelectedDay] = useState('All');
    const [selectedTeacher, setSelectedTeacher] = useState('All');
    const [sortConfig, setSortConfig] = useState({ key: 'day', direction: 'ascending' });
    const [selectedStudentModal, setSelectedStudentModal] = useState(null);

    // Helper to normalize day names (removes "วัน" prefix)
    const normalizeDay = (day) => {
        if (!day || typeof day !== 'string') return '-';
        const trimmed = day.trim();
        return trimmed.startsWith('วัน') ? trimmed.replace('วัน', '') : trimmed;
    };

    // Helper to normalize time for correct sorting
    const normalizeTimeForSort = (timeStr) => {
        if (!timeStr || typeof timeStr !== 'string') return timeStr;
        const parts = timeStr.split('-');
        let startTime = parts[0].trim();
        // If 4 chars (e.g., "9:00"), prepend "0" -> "09:00"
        if (startTime.length === 4) {
            startTime = '0' + startTime;
        }
        return parts.length > 1 ? `${startTime}-${parts[1].trim()}` : startTime;
    };

    // Helper to format date to Buddhist Year (DD/MM/BE)
    const formatToBuddhistDate = (dateVal) => {
        if (!dateVal) return '-';
        
        let date;
        if (dateVal.toDate && typeof dateVal.toDate === 'function') {
            date = dateVal.toDate(); // Handle Firestore Timestamp
        } else {
            date = new Date(dateVal);
        }

        if (isNaN(date.getTime())) return String(dateVal); // Fallback to raw value if invalid

        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear() + 543;
        return `${d}/${m}/${y}`;
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch required collections
                const [timeSheetSnap, courseSnap, receiptSnap, schoolTimeSheetSnap, schoolCourseSnap] = await Promise.all([
                    getDocs(collection(db, "time-sheet")),
                    getDocs(collection(db, "course-ID")),
                    getDocs(collection(db, "office-recipt")),
                    getDocs(collection(db, "school-timesheet")),
                    getDocs(collection(db, "school-course-ID"))
                ]);

                // 1. Course ID Mapping (Master Data)
                const catalog = {};
                [...courseSnap.docs, ...schoolCourseSnap.docs].forEach(doc => {
                    const data = doc.data();
                    const courseId = String(data['course-ID'] || data['school-course-ID'] || doc.id).trim();
                    const courseName = data['course'] || data['courseName'] || 'Unknown Course';
                    
                    // Parse periods (array or indexed object)
                    const masterPeriods = [];
                    if (data.period) {
                        if (Array.isArray(data.period)) {
                            data.period.forEach(p => {
                                if (p.day) masterPeriods.push({
                                    day: normalizeDay(p.day),
                                    time: p.time || '-',
                                    teacher: p.teacher || '-'
                                });
                            });
                        } else if (typeof data.period === 'object') {
                            let i = 1;
                            while (data.period[`day-${i}`] !== undefined) {
                                masterPeriods.push({
                                    day: normalizeDay(data.period[`day-${i}`]),
                                    time: data.period[`time-${i}`] || '-',
                                    teacher: data.period[`teacher-${i}`] || '-'
                                });
                                i++;
                            }
                        }
                    } else if (data.day) {
                        // Flat structure fallback
                        masterPeriods.push({
                            day: normalizeDay(data.day),
                            time: data.time || '-',
                            teacher: data.teacher || '-'
                        });
                    }

                    // Robust course-end extraction
                    // Robust course-end extraction
                    const courseEnd = data['course-end'] || data['courseEnd'] || data['course_end'] || '';
                    const courseType = String(data.courseType || data.type || (courseName.toLowerCase().includes('private') ? 'private' : 'class')).trim().toLowerCase();
                    const endRaw = String(courseEnd);
                    const isHour = data.isHour === true || data.isHour === 'true' || endRaw.includes('ชม') || endRaw.includes('ชั่วโมง');

                    catalog[courseId] = { 
                        name: courseName, 
                        periods: masterPeriods, 
                        courseEnd: courseEnd,
                        courseType: courseType,
                        isHour: isHour
                    };
                });

                // 2. Receipt Mapping
                const rMap = {};
                receiptSnap.docs.forEach(doc => {
                    const data = doc.data();
                    const receiptId = String(data['office-ID'] || doc.id).trim();
                    rMap[receiptId] = data;
                });

                const allTimetableDocs = [
                    ...timeSheetSnap.docs.map(doc => ({ id: doc.id, sourceType: 'Office', ...doc.data() })),
                    ...schoolTimeSheetSnap.docs.map(doc => ({ id: doc.id, sourceType: 'School', ...doc.data() }))
                ];

                setTimetableData(allTimetableDocs);
                setCourseCatalog(catalog);
                setReceiptMap(rMap);
            } catch (error) {
                console.error("Error fetching data:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'เกิดข้อผิดพลาด',
                    text: 'ไม่สามารถโหลดข้อมูลได้',
                    confirmButtonColor: '#3b82f6'
                });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Grouping and Filtering logic
    const processedTimetable = useMemo(() => {
        const groups = {};

        // 1. Iterate through time-sheet (timetableData) as the primary source
        timetableData.forEach(slot => {
            const courseId = String(slot['courseID'] || slot['course-ID'] || '').trim();
            const receiptId = String(slot['reciptID'] || '').trim();
            
            // Get master course details
            const masterCourse = courseCatalog[courseId];
            if (!masterCourse) return; // Skip if no matching course-ID metadata

            // Process each period defined for this course in master data
            masterCourse.periods.forEach((p, pIdx) => {
                const periodIndex = pIdx + 1;

                // Filter by slot's specific day if it exists (crucial for school classes)
                const slotDay = normalizeDay(slot.day || slot.Day);
                if (slotDay && slotDay !== '-' && slotDay !== p.day) return;

                // Determine the actual teacher for this slot (priority: slot data > master period)
                const actualTeacher = (slot.teacher || slot['teacher-name'] || p.teacher || '-').trim();
                
                // Determine grouping strategy based on course type
                const courseNameLower = masterCourse.name.toLowerCase();
                const isPrivate = masterCourse.courseType === 'private' || 
                                  masterCourse.isHour || 
                                  courseNameLower.includes('private') || 
                                  courseNameLower.includes('ส่วนตัว') || 
                                  courseNameLower.includes('รายชั่วโมง');
                
                // Extract student name early for identity
                let fullName = '';
                if (slot.sourceType === 'School' && slot.level) {
                    fullName = `ชั้นเรียน ${slot.level}`;
                } else {
                    const receiptData = receiptMap[receiptId];
                    if (receiptData) {
                        const name = (receiptData['name'] || '').trim();
                        const surname = (receiptData['surname'] || '').trim();
                        const nickname = receiptData['nickname'] ? `(${receiptData['nickname']})` : '';
                        fullName = `${name} ${surname} ${nickname}`.trim();
                    } else {
                        const k = periodIndex;
                        const set = { 
                            name: slot[`name-${k}`] || slot[`name${k}`] || slot.timesheet?.[`name-${k}`] || slot.timesheet?.[`name${k}`],
                            nickname: slot[`nickname-${k}`] || slot[`nickname${k}`] || slot.timesheet?.[`nickname-${k}`] || slot.timesheet?.[`nickname${k}`]
                        };
                        const name = (set.name || '').trim();
                        const nickname = set.nickname ? `(${set.nickname})` : '';
                        fullName = `${name} ${nickname}`.trim();
                    }
                }

                // Create the appropriate group key
                let groupKey;
                if (isPrivate) {
                    // For Private: Group by Student + Course (to merge their days/times)
                    const studentIdentity = receiptId || fullName;
                    groupKey = `private|${masterCourse.name}|${studentIdentity}`;
                } else {
                    // For Class: Group by Session (Day + Time + Teacher)
                    groupKey = `class|${masterCourse.name}|${p.day}|${p.time}|${actualTeacher}`;
                }

                // Initialize group if doesn't exist
                if (!groups[groupKey]) {
                    groups[groupKey] = {
                        courseId: courseId,
                        course: masterCourse.name,
                        daySet: new Set(),
                        timeSet: new Set(),
                        teacherSet: new Set(),
                        totalSessions: String(masterCourse.courseEnd || '0').replace(/[^0-9.]/g, '') || '0',
                        studentList: [], // { fullName, checkSum }
                        periodIndex: periodIndex,
                        courseType: masterCourse.courseType
                    };
                }

                const group = groups[groupKey];
                group.daySet.add(p.day);
                group.timeSet.add(p.time);
                group.teacherSet.add(actualTeacher);

                let checkSum = 0;
                let lastCheckInTimestamp = 0;
                let attemptCount = 0;

                const parseBEDateToTimestamp = (dateStr) => {
                    if (!dateStr || typeof dateStr !== 'string' || !dateStr.includes('/')) return 0;
                    const parts = dateStr.split('/');
                    if (parts.length !== 3) return 0;
                    const d = parseInt(parts[0], 10);
                    const m = parseInt(parts[1], 10) - 1;
                    let y = parseInt(parts[2], 10);
                    if (y < 100) y += 2500; 
                    if (y > 2500) y -= 543;
                    const date = new Date(y, m, d);
                    return isNaN(date.getTime()) ? 0 : date.getTime();
                };

                const processedIndices = new Set();
                [slot, slot.timesheet || {}].forEach(source => {
                    Object.keys(source).forEach(key => {
                        if (key.startsWith('check-')) {
                            const idx = key.split('-')[1];
                            if (processedIndices.has(idx)) return;
                            processedIndices.add(idx);

                            const val = source[key];
                            let isValidForCount = false;
                            if (val !== '' && val !== null && val !== undefined) {
                                if (val !== 'ยกคลาส') {
                                    attemptCount++;
                                }
                                const numVal = parseFloat(val);
                                if (!isNaN(numVal)) {
                                    checkSum += numVal;
                                    isValidForCount = true;
                                } else if (val && val !== 'ยกคลาส') {
                                    checkSum += 1;
                                    isValidForCount = true;
                                }
                            }
                            if (isValidForCount) {
                                const dateStr = source[`date-${idx}`];
                                if (dateStr) {
                                    const ts = parseBEDateToTimestamp(dateStr);
                                    if (ts > lastCheckInTimestamp) {
                                        lastCheckInTimestamp = ts;
                                    }
                                }
                            }
                        }
                    });
                });


                if (fullName) {
                    const totalLimit = parseFloat(group.totalSessions) || 0;
                    const isComplete = totalLimit > 0 && checkSum >= totalLimit;
                    
                    if (isComplete) {
                        const now = new Date();
                        const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
                        const gracePeriodInMs = 30 * 24 * 60 * 60 * 1000; // แสดงต่อไปอีก 30 วัน
                        if (todayMidnight > lastCheckInTimestamp + gracePeriodInMs) {
                            return; // ซ่อนนักเรียนหลังจากผ่านไป 30 วันนับจากวันที่เรียนครบ
                        }
                    }

                    // For students in merged rows, we distinguish between different enrollments (receipts)
                    const existingIdx = group.studentList.findIndex(s => s.fullName === fullName && s.receiptId === receiptId);
                    const receiptDataForHour = receiptMap[receiptId] || {};
                    const receiptCourseEnd = String(receiptDataForHour['course-end'] || receiptDataForHour['courseEnd'] || '');
                    const slotCourseEnd = String(slot['course-end'] || slot['courseEnd'] || '');
                    const studentIsHour = masterCourse.isHour || 
                                          receiptDataForHour.isHour === true || 
                                          receiptDataForHour.isHour === 'true' || 
                                          receiptCourseEnd.includes('ชม') || 
                                          receiptCourseEnd.includes('ชั่วโมง') || 
                                          receiptCourseEnd.includes('ช.ม.') || 
                                          slotCourseEnd.includes('ชม') || 
                                          slotCourseEnd.includes('ชั่วโมง') || 
                                          slotCourseEnd.includes('ช.ม.') || 
                                          String(masterCourse.name).includes('รายชั่วโมง');

                    const newStudentEntry = { 
                        fullName, 
                        receiptId,
                        checkCount: checkSum,
                        attemptCount,
                        isComplete,
                        updatedAt: slot.update || slot.updated || null,
                        timeSheetData: slot,
                        courseName: masterCourse.name,
                        isHour: studentIsHour
                    };

                    if (existingIdx === -1) {
                        group.studentList.push(newStudentEntry);
                    } else {
                        const existing = group.studentList[existingIdx];
                        // If current is active but existing is complete, replace existing
                        if (!isComplete && existing.isComplete) {
                            group.studentList[existingIdx] = newStudentEntry;
                        } 
                        // Or if both are same status, pick the one with more progress or more recent update
                        else if (isComplete === existing.isComplete) {
                            if (checkSum > existing.checkCount) {
                                group.studentList[existingIdx] = newStudentEntry;
                            }
                        }
                    }
                }
            });
        });

        // Convert Sets to sorted strings for display
        const result = Object.values(groups).map(g => {
            const dayOrder = { 'จันทร์': 1, 'อังคาร': 2, 'พุธ': 3, 'พฤหัสบดี': 4, 'ศุกร์': 5, 'เสาร์': 6, 'อาทิตย์': 7 };
            const sortedDays = Array.from(g.daySet).sort((a, b) => (dayOrder[a] || 99) - (dayOrder[b] || 99));
            const sortedTimes = Array.from(g.timeSet).sort();
            const sortedTeachers = Array.from(g.teacherSet).filter(t => t !== '-').sort();

            return {
                ...g,
                day: sortedDays.join(', '),
                time: sortedTimes.join(', '),
                teacher: sortedTeachers.length > 0 ? sortedTeachers.join(', ') : '-'
            };
        }).filter(group => group.studentList.length > 0);

        // Sort by Day and Time
        const dayOrder = { 'จันทร์': 1, 'อังคาร': 2, 'พุธ': 3, 'พฤหัสบดี': 4, 'ศุกร์': 5, 'เสาร์': 6, 'อาทิตย์': 7 };
        result.sort((a, b) => {
            const dayA = dayOrder[a.day] || 99;
            const dayB = dayOrder[b.day] || 99;
            if (dayA !== dayB) return dayA - dayB;
            
            // Normalize time for comparison (e.g., "9:00" -> "09:00")
            const timeA = normalizeTimeForSort(a.time);
            const timeB = normalizeTimeForSort(b.time);
            return timeA.localeCompare(timeB);
        });

        return result;
    }, [timetableData, courseCatalog, receiptMap]);

    // Extract unique filter options for dropdowns
    const filterOptions = useMemo(() => {
        const courses = new Set();
        const days = new Set();
        const teachers = new Set();

        processedTimetable.forEach(item => {
            courses.add(item.course);
            days.add(item.day);
            teachers.add(item.teacher);
        });

        return {
            courses: ['All', ...Array.from(courses).sort()],
            days: ['All', ...Array.from(days).sort((a, b) => {
                const dayOrder = { 'จันทร์': 1, 'อังคาร': 2, 'พุธ': 3, 'พฤหัสบดี': 4, 'ศุกร์': 5, 'เสาร์': 6, 'อาทิตย์': 7 };
                return (dayOrder[a] || 99) - (dayOrder[b] || 99);
            })],
            teachers: ['All', ...Array.from(teachers).sort()]
        };
    }, [processedTimetable]);

    // Apply selected filters to the grouped data
    const filteredTimetable = useMemo(() => {
        let result = processedTimetable;

        if (selectedCourse !== 'All') {
            result = result.filter(item => item.course === selectedCourse);
        }
        if (selectedDay !== 'All') {
            result = result.filter(item => item.day === selectedDay);
        }
        if (selectedTeacher !== 'All') {
            result = result.filter(item => item.teacher === selectedTeacher);
        }

        return result;
    }, [processedTimetable, selectedCourse, selectedDay, selectedTeacher]);

    // Sorting logic applied after filtering
    const sortedTimetable = useMemo(() => {
        let sortableItems = [...filteredTimetable];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue, bValue;

                switch (sortConfig.key) {
                    case 'course':
                        aValue = a.course;
                        bValue = b.course;
                        break;
                    case 'day':
                        const dayOrder = { 'จันทร์': 1, 'อังคาร': 2, 'พุธ': 3, 'พฤหัสบดี': 4, 'ศุกร์': 5, 'เสาร์': 6, 'อาทิตย์': 7 };
                        aValue = dayOrder[a.day] || 99;
                        bValue = dayOrder[b.day] || 99;
                        break;
                    case 'time':
                        aValue = normalizeTimeForSort(a.time);
                        bValue = normalizeTimeForSort(b.time);
                        break;
                    case 'teacher':
                        aValue = a.teacher;
                        bValue = b.teacher;
                        break;
                    case 'students':
                        // Sort by first student name
                        aValue = a.studentList?.[0]?.fullName || '';
                        bValue = b.studentList?.[0]?.fullName || '';
                        break;
                    case 'status':
                        // Sort by first student's progress (checkCount)
                        aValue = parseFloat(a.studentList?.[0]?.checkCount) || 0;
                        bValue = parseFloat(b.studentList?.[0]?.checkCount) || 0;
                        break;
                    case 'updated':
                        // Convert Firestore timestamp or date to numeric value
                        const getTime = (val) => {
                            if (!val) return 0;
                            if (val.toMillis) return val.toMillis();
                            if (val.seconds) return val.seconds * 1000;
                            return new Date(val).getTime() || 0;
                        };
                        aValue = getTime(a.studentList?.[0]?.updatedAt);
                        bValue = getTime(b.studentList?.[0]?.updatedAt);
                        break;
                    default:
                        aValue = a[sortConfig.key];
                        bValue = b[sortConfig.key];
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredTimetable, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <span className="sort-indicator">↕</span>;
        return sortConfig.direction === 'ascending' ? <span className="sort-indicator active">↑</span> : <span className="sort-indicator active">↓</span>;
    };

    // Grouping helper (could be used for more advanced layouts later)

    if (loading) {
        return (
            <div className="loading-container">
                <span className="loader"></span>
                <p>กำลังโหลดตารางสอน...</p>
            </div>);
    }

    return (
        <div className="timetable-page">
            <header className="timetable-header">
                <div className="header-logo-container">
                    <Link to="/">
                        <img src="/Wisdom-Logo.png" alt="Wisdom Logo" className="header-logo" />
                    </Link>
                </div>
                <div className="header-title-container">
                    <h1 className="timetable-title">ตารางสอน (Time-Sheet)</h1>
                    <p className="timetable-subtitle">ข้อมูลจากระบบจัดการตารางสอน</p>
                </div>
                <div className="header-filters-container-in-header">
                    <div className="filter-group">
                        <label>วิชา:</label>
                        <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)}>
                            {filterOptions.courses.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>วัน:</label>
                        <select value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)}>
                            {filterOptions.days.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>ครู:</label>
                        <select value={selectedTeacher} onChange={(e) => setSelectedTeacher(e.target.value)}>
                            {filterOptions.teachers.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                </div>
            </header>

            <div className="timetable-content">
                {filteredTimetable.length > 0 ? (
                    <>
                        {/* Desktop Table View */}
                        <div className="table-wrapper desktop-only-table">
                            <table className="timetable-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => requestSort('course')} className="sortable-header">รายวิชา (Course) {getSortIndicator('course')}</th>
                                        <th onClick={() => requestSort('day')} className="sortable-header">วัน (Day) {getSortIndicator('day')}</th>
                                        <th onClick={() => requestSort('time')} className="sortable-header">เวลา (Time) {getSortIndicator('time')}</th>
                                        <th onClick={() => requestSort('teacher')} className="sortable-header">ครูผู้สอน (Teacher) {getSortIndicator('teacher')}</th>
                                        <th onClick={() => requestSort('students')} className="sortable-header">รายชื่อนักเรียน (Students) {getSortIndicator('students')}</th>
                                        <th onClick={() => requestSort('status')} className="sortable-header">สถานะ {getSortIndicator('status')}</th>
                                        <th onClick={() => requestSort('updated')} className="sortable-header">Updated {getSortIndicator('updated')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedTimetable.map((group, index) => (
                                        <tr key={index}>
                                            <td style={{ fontWeight: 600, color: '#3b82f6' }}>{group.course}</td>
                                            <td>
                                                <span style={{ fontWeight: 500 }}>{group.day}</span>
                                            </td>
                                            <td>{group.time}</td>
                                            <td>{group.teacher}</td>
                                            <td className="students-cell">
                                                {group.studentList.length > 0 ? (
                                                    <ul className="student-list">
                                                        {group.studentList.map((student, sIndex) => {
                                                            const isOver13 = student.attemptCount > 13;
                                                            return (
                                                                <li 
                                                                    key={sIndex}
                                                                    className="clickable-student"
                                                                    onClick={() => setSelectedStudentModal(student)}
                                                                    style={isOver13 ? { backgroundColor: '#fce7f3', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', marginBottom: '2px', border: '1px solid #fbcfe8' } : {}}
                                                                >
                                                                    {student.fullName}
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                ) : (
                                                    <span className="no-students">ไม่มีนักเรียนลงทะเบียน</span>
                                                )}
                                            </td>
                                            <td className="status-cell">
                                                {group.studentList.length > 0 ? (
                                                    <ul className="student-list">
                                                        {group.studentList.map((student, sIndex) => (
                                                            <li key={sIndex} className="status-list-item">
                                                                {(() => {
                                                                    const total = parseFloat(group.totalSessions) || 0;
                                                                    const check = parseFloat(student.checkCount) || 0;
                                                                    let statusClass = "status-active";
                                                                    
                                                                    if (total > 0) {
                                                                        if (check >= total) statusClass = "status-complete";
                                                                        else if (check >= total - 1) statusClass = "status-warning-orange";
                                                                        else if (check >= total - 2) statusClass = "status-warning-yellow";
                                                                    }
                                                                    
                                                                    return (
                                                                        <span className={`status-badge ${statusClass}`}>
                                                                            {student.checkCount} / {group.totalSessions || '0'}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <span className="status-badge status-active">0 / {group.totalSessions || '0'}</span>
                                                )}
                                            </td>
                                            <td>
                                                {group.studentList.length > 0 ? (
                                                    <ul className="student-list">
                                                        {group.studentList.map((student, sIndex) => (
                                                            <li key={sIndex} className="status-list-item">
                                                                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                                    {formatToBuddhistDate(student.updatedAt)}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <span className="status-badge status-active">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards View */}
                        <div className="mobile-only-cards">
                            {sortedTimetable.map((group, index) => (
                                <div key={index} className="timetable-card">
                                    <div className="card-header">
                                        <h3 className="card-course-title">{group.course}</h3>
                                    </div>
                                    <div className="card-details">
                                        <div className="card-detail-item">
                                            <span className="detail-label">วัน</span>
                                            <span className="detail-value highlight-day">{group.day}</span>
                                        </div>
                                        <div className="card-detail-item">
                                            <span className="detail-label">เวลา</span>
                                            <span className="detail-value">{group.time}</span>
                                        </div>
                                        <div className="card-detail-item">
                                            <span className="detail-label">ผู้สอน</span>
                                            <span className="detail-value">{group.teacher}</span>
                                        </div>
                                    </div>
                                    <div className="card-students-section">
                                        <h4 className="students-section-title">รายชื่อนักเรียน & สถานะ</h4>
                                        {group.studentList.length > 0 ? (
                                            <div className="card-student-list">
                                                {group.studentList.map((student, sIndex) => {
                                                    const total = parseFloat(group.totalSessions) || 0;
                                                    const check = parseFloat(student.checkCount) || 0;
                                                    let statusClass = "status-active";
                                                    
                                                    if (total > 0) {
                                                        if (check >= total) statusClass = "status-complete";
                                                        else if (check >= total - 1) statusClass = "status-warning-orange";
                                                        else if (check >= total - 2) statusClass = "status-warning-yellow";
                                                    }
                                                    
                                                    return (
                                                        <div 
                                                            key={sIndex} 
                                                            className="card-student-row" 
                                                            onClick={() => setSelectedStudentModal(student)}
                                                            style={student.attemptCount > 13 ? { backgroundColor: '#fce7f3', borderRadius: '8px', border: '1px solid #fbcfe8' } : {}}
                                                        >
                                                            <div className="card-student-info">
                                                                <span className="card-student-name">{student.fullName}</span>
                                                                <span className="card-student-updated">อัปเดต: {formatToBuddhistDate(student.updatedAt)}</span>
                                                            </div>
                                                            <div className="card-student-status">
                                                                <span className={`status-badge ${statusClass}`}>
                                                                    {student.checkCount} / {group.totalSessions || '0'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="no-students">ไม่มีนักเรียนลงทะเบียน</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="no-data">
                        <EventNoteIcon />
                        <p>ไม่พบข้อมูลตารางสอน</p>
                    </div>
                )}
            </div>

            <footer style={{ marginTop: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                &copy; {new Date().getFullYear()} Wisdom Office Management System
            </footer>

            {/* Student Modal */}
            {selectedStudentModal && (
                <div className="modal-overlay" onClick={() => setSelectedStudentModal(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>ข้อมูลนักเรียน</h3>
                            <button className="close-modal-btn" onClick={() => setSelectedStudentModal(null)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-info-row">
                                <span className="modal-info-label">ชื่อ-นามสกุล</span>
                                <span className="modal-info-value">{selectedStudentModal.fullName}</span>
                            </div>
                            <div className="modal-info-row">
                                <span className="modal-info-label">รหัสใบเสร็จ (Receipt ID)</span>
                                <span className="modal-info-value">{selectedStudentModal.receiptId || '-'}</span>
                            </div>
                            <div className="modal-info-row">
                                <span className="modal-info-label">วิชา (Course)</span>
                                <span className="modal-info-value">{selectedStudentModal.courseName || '-'}</span>
                            </div>
                            
                            <div className="modal-history-list">
                                <h4>ประวัติการเข้าเรียน</h4>
                                {(() => {
                                    const slot = selectedStudentModal.timeSheetData;
                                    if (!slot) return <p style={{ color: '#64748b' }}>ไม่พบข้อมูลประวัติ</p>;
                                    
                                    const source = slot.timesheet || slot;
                                    
                                    const parseDateToTime = (dateStr) => {
                                        if (!dateStr || !dateStr.includes('/')) return 0;
                                        const parts = dateStr.split('/');
                                        if (parts.length !== 3) return 0;
                                        const d = parseInt(parts[0], 10);
                                        const m = parseInt(parts[1], 10) - 1;
                                        let y = parseInt(parts[2], 10);
                                        if (y < 100) y += 2500; 
                                        if (y > 2500) y -= 543;
                                        return new Date(y, m, d).getTime();
                                    };

                                    const rawItems = [];
                                    for (let i = 1; i <= 100; i++) {
                                        const dateKey = `date-${i}`;
                                        const checkKey = `check-${i}`;
                                        if (source[dateKey]) {
                                            rawItems.push({
                                                originalIndex: i,
                                                dateStr: source[dateKey],
                                                timestamp: parseDateToTime(source[dateKey]),
                                                status: source[checkKey],
                                                comment: source[`comment-${i}`] || source[`comment${i}`],
                                                nextComment: source[`nextcomment-${i}`] || source[`nextcomment${i}`],
                                                teacher: source[`teacher-${i}`] || source[`teacher${i}`] || slot.teacher || slot['teacher-name'] || '-'
                                            });
                                        }
                                    }

                                    // Sort by timestamp from earliest to latest
                                    rawItems.sort((a, b) => a.timestamp - b.timestamp);

                                    let displayIndex = 0;
                                    const historyItems = rawItems.map((item, index) => {
                                        let statusClass = "history-status";
                                        let statusText = item.status;
                                        
                                        let isCountable = false;

                                        if (item.status === '1' || item.status === 1) {
                                            statusText = "มาเรียน";
                                            statusClass += " present";
                                            isCountable = true;
                                        } else if (item.status === '0' || item.status === 0) {
                                            statusText = "ขาดเรียน";
                                            statusClass += " absent";
                                        } else if (item.status === 'ยกคลาส') {
                                            statusClass += " cancel";
                                        } else {
                                            const numStatus = Number(item.status);
                                            if (item.status !== undefined && item.status !== null && item.status !== '' && !isNaN(numStatus) && numStatus > 0) {
                                                isCountable = true;
                                            }
                                        }
                                        
                                        let currentDisplayIndex = "-";
                                        if (isCountable) {
                                            if (selectedStudentModal.isHour) {
                                                let numStatus = Number(item.status);
                                                if (isNaN(numStatus) || numStatus <= 0) numStatus = 1;
                                                displayIndex += numStatus;
                                                currentDisplayIndex = parseFloat(displayIndex.toFixed(2));
                                            } else {
                                                displayIndex++;
                                                currentDisplayIndex = displayIndex;
                                            }
                                        }
                                        
                                        return (
                                            <div className="history-item" key={index}>
                                                <div className="history-item-header">
                                                    <span className="history-date">{selectedStudentModal.isHour ? 'ชั่วโมงที่' : 'ครั้งที่'} {currentDisplayIndex} : {item.dateStr}</span>
                                                    <span className={statusClass}>{statusText || '-'}</span>
                                                </div>
                                                {(item.comment || item.nextComment || (item.teacher && item.teacher !== '-')) && (
                                                    <div className="history-item-details">
                                                        {item.teacher && item.teacher !== '-' && <p><strong>ครูผู้สอน:</strong> {item.teacher}</p>}
                                                        {item.comment && <p><strong>ความคิดเห็น:</strong> {item.comment}</p>}
                                                        {item.nextComment && <p><strong>สำหรับครั้งต่อไป:</strong> {item.nextComment}</p>}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    });
                                    
                                    if (historyItems.length === 0) {
                                        return <p style={{ color: '#64748b' }}>ยังไม่มีประวัติการเข้าเรียน</p>;
                                    }
                                    return historyItems;
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Timetable;
